<script setup lang="ts">
import { useBreakpoints } from '@vueuse/core';

import ContentSection from './levels/ContentSection.vue';
import AvatarSection from './levels/AvatarSection.vue';
import ProgressSection from './levels/ProgressSection.vue';

const breakpoints = useBreakpoints({
  mobile: 768,
});

const isMobile = breakpoints.smaller('mobile');
const { gamificationName, gamificationCoinName } =
  storeToRefs(useSmarticoConfigs());

const {
  userInfosProfile,
  progressToAnotherLevel,
  currentUserLevelName,
  currentUserLevelImage,
  userLevelNumber,
  nextUserLevel,
  currentUserLevelData,
} = storeToRefs(useSmarticoUserInfos());

const { allLevels } = storeToRefs(useSmarticoLevels());

const { openChangeAvatar, loadUserInfosProfile } = useSmarticoUserInfos();

const openModal = ref<boolean>(false);
const selectedLevel = ref<TLevel>();

function openLevelDetails(level: TLevel) {
  selectedLevel.value = level;
  openModal.value = true;
}

function handleAvatarPopup(e: any) {
  e.preventDefault();
  e.stopPropagation();
  openChangeAvatar();
}

const isReloadingPage = ref(false);

function handleUpdateUserProfile(e: any) {
  isReloadingPage.value = true;
  e.preventDefault();
  e.stopPropagation();
  loadUserInfosProfile().then(() => {
    setTimeout(() => {
      isReloadingPage.value = false;
    }, 300);
  });
}

const userPointsInLevel = computed(() => {
  if (!userInfosProfile.value || !currentUserLevelData.value) return 0;

  return (
    userInfosProfile.value.ach_points_ever -
    currentUserLevelData.value.required_points
  );
});

const pointsToNextLevel = computed(() => {
  if (!nextUserLevel.value || !currentUserLevelData.value) return 0;

  return (
    nextUserLevel.value.required_points -
    currentUserLevelData.value.required_points
  );
});
</script>

<template>
  <div
    v-if="userInfosProfile"
    :class="$style.smarticoLevels"
  >
    <div v-if="isReloadingPage">
      <div
        class="load skeleton-loading"
        style="height: 350px"
      ></div>
    </div>
    <div
      v-else
      :class="$style.mainLevel"
    >
      <div :class="$style.avatarLevel">
        <AvatarSection
          :current-user-level-image="currentUserLevelImage"
          :current-user-level-name="currentUserLevelName"
          :user-infos-profile="userInfosProfile"
          @edit-avatar="handleAvatarPopup"
          @update-profile="handleUpdateUserProfile"
        />
        <ProgressSection
          v-if="nextUserLevel"
          :next-user-level="nextUserLevel"
          :points-to-next-level="pointsToNextLevel"
          :progress-to-another-level="progressToAnotherLevel"
          :user-points-in-level="userPointsInLevel"
        />
      </div>
      <ContentSection
        v-if="!isMobile"
        :gamification-coin-name="gamificationCoinName"
        :gamification-name="gamificationName"
      />
      <div
        v-else
        class="pt-4"
      >
        <SmarticoBaseLevelsSlide
          class="bg-transparent"
          :levels="allLevels || []"
          :user-level-number="userLevelNumber"
          @open-level-details="openLevelDetails"
        />
        <ContentSection
          class="p2 bg-black/30 rounded-lg mt-4"
          :gamification-coin-name="gamificationCoinName"
          :gamification-name="gamificationName"
        />
      </div>
    </div>

    <div
      v-if="isReloadingPage || !allLevels"
      :class="$style.levelList"
    >
      <div
        v-for="(n, index) in 5"
        :key="`skeleton-${index}`"
        class="load skeleton-loading"
        style="height: 220px; margin-top: 30px"
      ></div>
    </div>
    <SmarticoBaseLevelsSlide
      v-else-if="allLevels && allLevels.length > 0 && !isMobile"
      :levels="allLevels"
      :user-level-number="userLevelNumber"
      @open-level-details="openLevelDetails"
    />
  </div>
  <BaseModal
    v-model="openModal"
    custom-class-wrapper="levelModalWrapper"
    :width="600"
  >
    <div
      v-if="selectedLevel"
      id="level-gamificationModal"
      class="p-6"
    >
      <img
        id="level-imageModal"
        alt="Level"
        class="my-0 mx-auto h-[250px]"
        :src="selectedLevel.image"
      />
      <div
        id="level-modalDescription"
        class="p-2 md:p-4 text-texts text-center flex flex-col"
      >
        <div
          id="level-timerModal"
          class="timerModal"
        ></div>
        <div
          id="level-titleModal"
          class="mb-1 md:mb-4 text-sm md:text-2xl px-2 font-bold"
          >{{ selectedLevel.name }}</div
        >
        <div
          id="level-descriptionModal"
          class="text-texts/70 text-xs md:text-sm leading-4 mb-4 md:mb-5 px-1 md:px-2 max-h-full overflow-y-auto overflow-x-hidden"
          v-html="selectedLevel.description"
        ></div>
      </div>
    </div>
  </BaseModal>
</template>

<style module lang="postcss">
.wrapper {
  @apply h-auto;
}
.smarticoLevels {
  @apply mb-10 w-full min-h-[560px];
  .mainLevel {
    @apply flex mb-5 flex-col md:flex-row relative overflow-hidden md:bg-black/30 rounded-lg bg-transparent;
    .avatarLevel {
      @apply relative p-4 flex flex-col items-center justify-center bg-gradient-to-b to-primary/20 from-primary/5 pb-2 min-w-full md:min-w-[300px];
      .buttonUpdate {
        @apply absolute top-4 left-4 text-texts/40 text-sm cursor-pointer;
      }

      .buttonEdit {
        @apply absolute top-4 right-4 text-texts/40 text-sm cursor-pointer;
      }

      .userAvatar {
        @apply bg-contain bg-no-repeat bg-center cursor-pointer mx-auto my-0 rounded-full w-[140px] h-[140px] md:w-[220px] md:h-[220px];
      }

      .levelBadge {
        @apply py-1.5 pl-2 pr-3 md:py-2 md:pl-3 md:pr-4 relative max-w-[100%] flex items-center justify-center bg-bg-primary rounded-full -mt-4 md:-mt-6 mx-auto z-10 text-center gap-1 text-texts text-xs md:text-sm font-semibold whitespace-nowrap;
        img {
          @apply w-[20px] md:w-[26px];
        }
      }

      .toNextLevel {
        @apply w-full max-w-[280px] mx-auto mt-4 flex flex-col items-center justify-center px-2;
        .titletoNext {
          @apply text-[.68rem] text-texts;
        }

        .userProgress {
          @apply relative w-full h-4 rounded-full overflow-hidden mt-1;
          .progressBar {
            @apply w-full h-full bg-texts/20 overflow-hidden;
          }
          .progressBarFill {
            @apply h-full bg-primary rounded-l;
          }
        }

        .levelsLabel {
          @apply flex justify-between items-center mt-0.5 w-full;
          .progressNumber {
            @apply text-texts font-semibold text-[.68rem] whitespace-nowrap;
          }
          .levelBadge {
            @apply p-0 bg-transparent max-w-[50%] m-0 gap-0.5 text-[.68rem];
            img {
              @apply w-[10px] md:w-[12px];
            }
          }
        }
      }
    }

    .mainLevelContent {
      @apply py-4 px-5 md:py-6 md:px-8;

      .mainLevelTitle {
        h1 {
          @apply mb-3 md:mb-6 text-texts text-base md:text-2xl font-bold;
        }
      }

      .mainLevelDescription {
        @apply max-h-[150px] md:max-h-none mb-4 md:mb-0 overflow-x-hidden overflow-y-auto;

        p {
          @apply mb-3 text-texts text-xs md:text-base leading-5 md:leading-6;
        }
      }

      .titleBgContent {
        .titleBg {
          @apply bg-button-bg text-button-text px-2 py-2 md:px-5 md:py-3 rounded inline-flex font-bold text-xs md:text-base w-full md:w-auto text-center justify-center items-center;
        }
      }
    }
  }

  .levelList {
    @apply grid grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5;
  }
}
</style>
